import React from "react";
import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";
import {
  FiCalendar,
  FiHash,
  FiMail,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiX,
  FiStar,
} from "react-icons/fi";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import NotificationModalSkeleton from "./NotificationModalSkeleton";

/* ========== KEYFRAMES ========== */
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ========== MIXINS ========== */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
`;

/* ========== STYLED COMPONENTS ========== */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(12px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalContainer = styled.div`
  position: relative;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  width: 100%;
  max-width: 680px;
  height: min(720px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 1px 0 0 rgba(255, 255, 255, 0.04) inset,
    0 24px 64px rgba(0, 0, 0, 0.45),
    0 8px 16px rgba(0, 0, 0, 0.18);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
    pointer-events: none;
    z-index: 2;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => surface(theme, 0)}cc;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: ${tokens.radiusLg};

  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid ${({ theme }) => hairline(theme, true)};
    border-top-color: ${tokens.brand};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 20px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .header-left {
    flex: 1;
    min-width: 0;
  }

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    .eyebrow {
      ${eyebrow};
      font-size: 0.58em;
    }

    h2 {
      margin: 2px 0 0 0;
      font-size: 1.05em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.015em;
      line-height: 1.3;
    }
  }

  .meta-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
    flex-wrap: wrap;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 0.78em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;

      svg { font-size: 0.9em; }
    }
  }

  .status-pills {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;

    button {
      ${mono};
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 5px 10px;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusPill};
      background: transparent;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.6em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover:not(.active) { opacity: 0.85; }

      &.active { opacity: 1; }

      &.nao_resolvido.active {
        border-color: rgba(239, 68, 68, 0.35);
        background: ${tokens.dangerSoft};
        color: ${tokens.danger};
      }
      &.em_analise.active {
        border-color: rgba(245, 158, 11, 0.35);
        background: ${tokens.warnSoft};
        color: ${tokens.warn};
      }
      &.resolvido.active {
        border-color: rgba(16, 185, 129, 0.35);
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
      }

      svg { font-size: 0.9em; }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;

    button {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      cursor: pointer;
      opacity: 0.65;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 1em; }

      &:hover {
        opacity: 1;
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.favorito { color: #f59e0b; opacity: 0.9; }
    }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 22px 14px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .section-header {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.5;
    padding: 0;

    svg { font-size: 1em; opacity: 0.7; }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const InfoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &:hover { border-color: ${({ theme }) => hairline(theme, true)}; }

  .info-icon {
    width: 28px;
    height: 28px;
    min-width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.brandSoft};
    border: 1px solid ${tokens.brandRing};
    border-radius: ${tokens.radiusSm};
    color: ${tokens.brand};
    font-size: 0.9em;
  }

  .info-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    .info-label {
      ${mono};
      font-size: 0.58em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .info-value {
      font-size: 0.82em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
      line-height: 1.3;
      word-break: break-word;
    }
  }
`;

const ContentBox = styled.div`
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  padding: 14px 16px;
  color: ${({ theme }) => theme.text};
  font-size: 0.85em;
  line-height: 1.6;

  p { margin: 0 0 0.6em 0; &:last-child { margin-bottom: 0; } }
  strong { font-weight: 600; color: ${tokens.brand}; }
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  button {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: ${tokens.radiusMd};
    font-size: 0.72em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    border: 1px solid transparent;

    svg { font-size: 1em; }

    &.btn-ghost {
      background: transparent;
      border-color: ${({ theme }) => hairline(theme)};
      color: ${({ theme }) => theme.text};
      opacity: 0.7;

      &:hover {
        opacity: 1;
        border-color: ${({ theme }) => hairline(theme, true)};
        background: ${({ theme }) => surface(theme, 1)};
      }
    }

    &.btn-primary {
      background: ${tokens.brand};
      color: #fff;
      border-color: ${tokens.brand};

      &:hover {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
      }
    }
  }
`;

/* ========== COMPONENT ========== */
export default function NotificationModal({ notification, loading, onClose, onUpdateStatus }) {
  const handleToggleFavorita = async () => {
    if (!notification) return;
    try {
      await onUpdateStatus({ favorita: !notification.favorita });
    } catch (error) {
      console.error("Erro ao atualizar favorita:", error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!notification) return;
    try {
      await onUpdateStatus({ status: newStatus });
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
    }
  };

  const formatDate = (dateStr) => dateStr || "N/A";

  if (!notification) return <NotificationModalSkeleton />;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {loading && (
          <LoadingOverlay>
            <div className="spinner" />
          </LoadingOverlay>
        )}

        <ModalHeader>
          <div className="header-left">
            <div className="title-block">
              <span className="eyebrow">NOTIFICAÇÃO · E-CAC</span>
              <h2>{notification.assunto}</h2>
            </div>
            <div className="meta-row">
              <span className="meta-item"><HiOutlineOfficeBuilding /> {notification.remetente}</span>
              <span className="meta-item"><FiCalendar /> {formatDate(notification.dataEnvio)}</span>
              {notification.protocolo && (
                <span className="meta-item"><FiHash /> {notification.protocolo}</span>
              )}
            </div>
            <div className="status-pills">
              <button className={`nao_resolvido ${(notification.status || 'nao_resolvido') === 'nao_resolvido' ? 'active' : ''}`} onClick={() => handleStatusChange('nao_resolvido')}>
                <FiAlertCircle /> Não Resolvido
              </button>
              <button className={`em_analise ${(notification.status || 'nao_resolvido') === 'em_analise' ? 'active' : ''}`} onClick={() => handleStatusChange('em_analise')}>
                <FiClock /> Em Análise
              </button>
              <button className={`resolvido ${(notification.status || 'nao_resolvido') === 'resolvido' ? 'active' : ''}`} onClick={() => handleStatusChange('resolvido')}>
                <FiCheckCircle /> Resolvido
              </button>
            </div>
          </div>
          <div className="header-actions">
            <button className={notification.favorita ? 'favorito' : ''} onClick={handleToggleFavorita} title="Favoritar">
              {notification.favorita ? <FiStar style={{ fill: '#f59e0b' }} /> : <FiStar />}
            </button>
            <button onClick={onClose} title="Fechar">
              <FiX />
            </button>
          </div>
        </ModalHeader>

        <ModalBody>
          <ScrollArea>
            <Section>
              <div className="section-header"><FiHash /> Informações</div>
              <InfoGrid>
                <InfoCard>
                  <div className="info-icon"><HiOutlineOfficeBuilding /></div>
                  <div className="info-content">
                    <span className="info-label">CNPJ</span>
                    <span className="info-value">{notification.cnpj || "N/A"}</span>
                  </div>
                </InfoCard>
                <InfoCard>
                  <div className="info-icon"><FiCalendar /></div>
                  <div className="info-content">
                    <span className="info-label">Data de Envio</span>
                    <span className="info-value">{formatDate(notification.dataEnvio)}</span>
                  </div>
                </InfoCard>
                <InfoCard>
                  <div className="info-icon"><FiClock /></div>
                  <div className="info-content">
                    <span className="info-label">Data de Validade</span>
                    <span className="info-value">{formatDate(notification.dataValidade)}</span>
                  </div>
                </InfoCard>
                <InfoCard>
                  <div className="info-icon"><FiFileText /></div>
                  <div className="info-content">
                    <span className="info-label">Data de Leitura</span>
                    <span className="info-value">{formatDate(notification.dataLeitura)}</span>
                  </div>
                </InfoCard>
              </InfoGrid>
            </Section>

            {notification.textoCompleto && (
              <Section>
                <div className="section-header"><FiMail /> Mensagem Original</div>
                <ContentBox dangerouslySetInnerHTML={{ __html: notification.textoCompleto }} />
              </Section>
            )}

            {notification.parecer && (
              <Section>
                <div className="section-header"><FiAlertCircle /> Parecer</div>
                <ContentBox>{notification.parecer}</ContentBox>
              </Section>
            )}

            {notification.consequencia && (
              <Section>
                <div className="section-header"><FiCheckCircle /> Consequências e Recomendações</div>
                <ContentBox>{notification.consequencia}</ContentBox>
              </Section>
            )}
          </ScrollArea>
        </ModalBody>

        <ModalFooter>
          <button className="btn-ghost" onClick={onClose}>Fechar</button>
          {(notification.status || 'nao_resolvido') !== 'resolvido' && (
            <button className="btn-primary" onClick={() => handleStatusChange('resolvido')}>
              <FiCheckCircle /> Marcar como Resolvido
            </button>
          )}
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}
